import * as React from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/server";
import { useTranslation } from "react-i18next";
import { ButtonSecondary } from "~/ui/infra/Button";

type PasskeyLoginButtonProps = {
  emailInputRef: React.RefObject<HTMLInputElement>;
  disabled?: boolean;
};

function isPasskeySupported() {
  return typeof window !== "undefined" && typeof window.PublicKeyCredential !== "undefined";
}

export function PasskeyLoginButton({ emailInputRef, disabled }: PasskeyLoginButtonProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const supported = isPasskeySupported();

  const signInWithPasskey = async () => {
    if (!supported || busy || disabled) return;

    const email = emailInputRef.current?.value?.trim().toLowerCase() ?? "";
    if (!email) {
      setError(t("common.email_required", { defaultValue: "Email is required." }));
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const startResponse = await fetch("/api/auth/passkey", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ operation: "login-start", email }),
      });
      const startPayload = (await startResponse.json()) as
        | { ok: true; credentialRequestOptions: PublicKeyCredentialRequestOptionsJSON }
        | { ok?: false; message?: string; formErrors?: string[] };

      if (!startResponse.ok || startPayload.ok !== true) {
        const failurePayload = startPayload as { message?: string; formErrors?: string[] };
        const message =
          failurePayload.message ||
          failurePayload.formErrors?.[0] ||
          t("app.passkey.unavailable", { defaultValue: "Unable to sign in with passkey." });
        setError(message);
        return;
      }

      const credential = await startAuthentication({ optionsJSON: startPayload.credentialRequestOptions });

      const finishResponse = await fetch("/api/auth/passkey", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operation: "login-finish",
          email,
          credential,
          rememberDevice: false,
        }),
      });

      if (finishResponse.redirected) {
        window.location.assign(finishResponse.url);
        return;
      }

      const finishPayload = (await finishResponse.json()) as { ok?: boolean; message?: string; formErrors?: string[] };
      if (!finishResponse.ok) {
        setError(
          finishPayload.message ||
            finishPayload.formErrors?.[0] ||
            t("app.passkey.unavailable", { defaultValue: "Unable to sign in with passkey." })
        );
        return;
      }

      window.location.assign("/dashboard");
    } catch (caught) {
      if (caught instanceof Error && caught.name === "NotAllowedError") {
        setError(t("app.passkey.cancelled", { defaultValue: "Passkey sign-in was cancelled." }));
        return;
      }
      setError(t("app.passkey.failed", { defaultValue: "Passkey sign-in failed. Try again or use your password." }));
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return null;
  }

  return (
    <div className="space-y-2">
      <ButtonSecondary type="button" className="my-2 w-full" onClick={signInWithPasskey} disabled={busy || disabled} isLoading={busy}>
        {t("app.passkey.sign_in", { defaultValue: "Sign in with passkey" })}
      </ButtonSecondary>
      {error ? <p className="text-center text-sm text-red-400">{error}</p> : null}
    </div>
  );
}
